import { lazy, Suspense, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useExtracted } from "next-intl";
import { cn } from "@/lib/utils";
import { WORKSPACE_ROUTES } from "@/lib/workspace-routes";
import { StrategyTabsProvider } from "@/hooks/StrategyTabsProvider";
import { TickerWorkspacesProvider } from "@/hooks/TickerWorkspacesProvider";
import { useAppHotkeys } from "@/hooks/useAppHotkeys";
import { useAuth } from "@/hooks/useAuth";
import { BacktestRunsProvider } from "@/hooks/useBacktestRuns";
import { useEventListener } from "@/hooks/useEventBus";
import { GuideChatProvider } from "@/hooks/useGuideChat";
import { useModalRouter } from "@/hooks/useModalRouter";
import {
  PortalSlotsProvider,
  useFooterSlotRef,
  useHeaderActionsSlotRef,
} from "@/hooks/usePortalSlots";
import { PositionsProvider } from "@/hooks/usePositions";
import { SavedStrategiesProvider } from "@/hooks/useSavedStrategies";
import { SimulatorTabsProvider } from "@/hooks/useSimulatorTabs";
import { AskAITrigger } from "@/components/guide/AskAITrigger";
import { ConfirmModal } from "@/components/ui/confirm-modal";
import { V3_ROUTES, v3PathForLegacy } from "@/features/v3/routes";
import { ExpiryBanner } from "./ExpiryBanner";
import { LocaleToggle } from "./LocaleToggle";
import { PageTransition } from "./PageTransition";

const AskAIModal = lazy(() =>
  import("@/components/guide/AskAIModal").then((module) => ({
    default: module.AskAIModal,
  })),
);
const InfractionModal = lazy(() =>
  import("./InfractionModal").then((module) => ({
    default: module.InfractionModal,
  })),
);
const SavedStrategiesPanel = lazy(() =>
  import("./SavedStrategiesPanel").then((module) => ({
    default: module.SavedStrategiesPanel,
  })),
);
const ShortcutsModal = lazy(() =>
  import("./ShortcutsModal").then((module) => ({
    default: module.ShortcutsModal,
  })),
);
const StructureTabs = lazy(() =>
  import("./StructureTabs").then((module) => ({
    default: module.StructureTabs,
  })),
);

interface AppShellProps {
  children: ReactNode;
  lightweight?: boolean;
}

// Authenticated chrome: sticky header with portal slots for per-page actions,
// sign-out confirm dialog, keyboard-shortcuts modal, and the membership expiry
// banner. Domain-specific modals
// (StructureTabs, AskAI, Upgrade, SavedStrategies, NotificationPanel, etc.)
// re-attach as their feature
// surfaces are ported.
export function AppShell({ children, lightweight = false }: AppShellProps) {
  if (lightweight) {
    return (
      <GuideChatProvider>
        <AppShellInner lightweight>{children}</AppShellInner>
      </GuideChatProvider>
    );
  }

  return (
    <SavedStrategiesProvider>
      <SimulatorTabsProvider>
        <StrategyTabsProvider>
          <TickerWorkspacesProvider>
            <BacktestRunsProvider>
              <PositionsProvider>
                <GuideChatProvider>
                  <AppShellInner>{children}</AppShellInner>
                </GuideChatProvider>
              </PositionsProvider>
            </BacktestRunsProvider>
          </TickerWorkspacesProvider>
        </StrategyTabsProvider>
      </SimulatorTabsProvider>
    </SavedStrategiesProvider>
  );
}

function AppShellInner({
  children,
  lightweight = false,
}: {
  children: ReactNode;
  lightweight?: boolean;
}) {
  const te = useExtracted();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useAppHotkeys();
  const [showLogout, setShowLogout] = useState(false);
  const [aiInitialQuestion, setAiInitialQuestion] = useState<
    string | undefined
  >();
  const [aiPromptKey, setAiPromptKey] = useState(0);
  const { modal, openModal, closeModal, setOpen } = useModalRouter();
  const strategiesPanelOpen = modal === "strategies";
  const aiModalOpen = modal === "guide";
  const immersiveWorkspace =
    pathname === "/workspace" ||
    pathname === "/home" ||
    pathname === "/kol" ||
    pathname.startsWith("/workspace/");
  const showHeader = !lightweight && !immersiveWorkspace;

  useEventListener("auth:logout", () => setShowLogout(true));
  useEventListener("strategies:open", () =>
    modal === "strategies" ? closeModal() : openModal("strategies"),
  );
  // 1.0 is admin-only; everywhere else the inbox is 3.0's.
  useEventListener("notifications:open", () =>
    navigate(
      user?.admin && v3PathForLegacy(pathname) != null
        ? WORKSPACE_ROUTES.inbox
        : V3_ROUTES.inbox,
    ),
  );
  useEventListener("ai-assistant:open", () =>
    modal === "guide" ? closeModal() : openModal("guide"),
  );
  useEventListener("ai-assistant:prompt", ({ question }) => {
    setAiInitialQuestion(question);
    setAiPromptKey((current) => current + 1);
    openModal("guide");
  });

  return (
    <PortalSlotsProvider>
      <div
        className={cn(
          "relative flex min-h-dvh min-w-0 flex-1 flex-col overflow-visible",
          !immersiveWorkspace && "pt-[env(safe-area-inset-top)]",
        )}
      >
        {!immersiveWorkspace && (
          <ExpiryBanner
            expiresAt={user?.expires_at ?? null}
            tier={user?.tier ?? null}
          />
        )}

        {showHeader && (
          <header className="bg-background/88 sticky top-[env(safe-area-inset-top)] z-40 flex h-14 shrink-0 items-center justify-between gap-0 px-3 backdrop-blur-xl">
            <div className="flex shrink-0 items-center gap-2">
              <div className="shrink-0" />
              <HeaderActionsSlot />
            </div>
            <Suspense fallback={null}>
              <StructureTabs />
            </Suspense>
            <div className="flex shrink-0 items-center gap-1">
              <AskAITrigger />
              <LocaleToggle />
            </div>
          </header>
        )}

        <main className="flex min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col overflow-visible">
            <PageTransition>{children}</PageTransition>
          </div>
          <FooterSlot />
        </main>
      </div>

      {!lightweight && (
        <Suspense fallback={null}>
          <ShortcutsModal />

          {strategiesPanelOpen ? (
            <SavedStrategiesPanel
              open
              onOpenChange={(next) => setOpen("strategies", next)}
            />
          ) : null}

          <InfractionModal />
        </Suspense>
      )}

      {aiModalOpen ? (
        <Suspense fallback={null}>
          <AskAIModal
            initialQuestion={aiInitialQuestion}
            promptKey={aiPromptKey}
          />
        </Suspense>
      ) : null}

      <ConfirmModal
        cancelLabel={te("Cancel")}
        confirmLabel={te("Sign Out")}
        destructive={false}
        message={te("Are you sure you want to sign out?")}
        open={showLogout}
        title={te("Sign Out")}
        onCancel={() => setShowLogout(false)}
        onConfirm={() => {
          setShowLogout(false);
          void logout();
        }}
      />
    </PortalSlotsProvider>
  );
}

function HeaderActionsSlot() {
  const callbackRef = useHeaderActionsSlotRef();
  return <div ref={callbackRef} className="flex items-center" />;
}

function FooterSlot() {
  const callbackRef = useFooterSlotRef();
  return <footer ref={callbackRef} className="shrink-0" />;
}
